import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Check, Loader2 } from "lucide-react";
import { submitReview } from "../lib/api";

const empty = { name: "", suburb: "", text: "" };

export const ReviewModal = ({ open, onClose }) => {
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    if (open) { setForm(empty); setStatus("idle"); }
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await submitReview(form);
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  const field = "w-full rounded-[16px] bg-sora-bg border border-sora-stone/50 px-4 py-3 text-sora-charcoal placeholder:text-sora-charcoal/40 focus:outline-none focus:border-sora-blue focus:ring-2 focus:ring-sora-blue/20 transition";

  return (
    <AnimatePresence>
      {open && (
        <motion.div data-testid="review-modal" className="fixed inset-0 z-[100] grid place-items-center p-4 md:p-6"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="absolute inset-0 bg-sora-slate/40 backdrop-blur-sm" onClick={onClose} />
          <motion.div className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-[28px] bg-sora-warm shadow-[0_30px_80px_rgba(63,88,115,0.25)] p-7 md:p-9"
            initial={{ scale: 0.94, y: 20, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.96, y: 10, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}>
            <button data-testid="review-close" onClick={onClose} className="absolute top-5 right-5 grid place-items-center h-9 w-9 rounded-full bg-sora-bg text-sora-slate hover:bg-sora-stone/40 transition">
              <X size={18} />
            </button>

            {status === "done" ? (
              <div className="text-center py-10" data-testid="review-success">
                <span className="mx-auto mb-5 grid place-items-center h-16 w-16 rounded-full bg-sora-sage/50 text-sora-slate"><Check size={30} strokeWidth={1.5} /></span>
                <h3 className="font-display text-2xl text-sora-slate mb-2">Thank you</h3>
                <p className="text-sora-charcoal/75 leading-relaxed max-w-sm mx-auto">
                  Thank you for your review — we're so grateful you took the time to share your experience.
                </p>
                <button data-testid="review-done" onClick={onClose} className="mt-6 bg-sora-blue text-white rounded-[16px] px-7 py-3 font-bold hover:bg-sora-slate transition">Close</button>
              </div>
            ) : (
              <>
                <p className="text-xs uppercase tracking-[0.22em] text-sora-blue font-bold mb-1">Leave a review</p>
                <h3 className="font-display text-2xl md:text-3xl text-sora-slate mb-1">Share your experience</h3>
                <p className="text-sm text-sora-charcoal/70 mb-6">Your words help other families feel confident reaching out.</p>
                <form onSubmit={submit} className="space-y-3.5">
                  <div className="grid sm:grid-cols-2 gap-3.5">
                    <input data-testid="review-name" required placeholder="Your name" value={form.name} onChange={set("name")} className={field} />
                    <input data-testid="review-suburb" placeholder="Your suburb" value={form.suburb} onChange={set("suburb")} className={field} />
                  </div>
                  <textarea data-testid="review-text" required rows={5} placeholder="Tell us about your experience with Sora Speech Co." value={form.text} onChange={set("text")} className={field} />
                  {status === "error" && <p data-testid="review-error" className="text-sm text-red-600">Something went wrong. Please try again.</p>}
                  <button data-testid="review-submit" disabled={status === "loading"} className="w-full inline-flex items-center justify-center gap-2 bg-sora-blue text-white rounded-[18px] px-6 py-3.5 font-bold hover:bg-sora-slate transition disabled:opacity-70">
                    {status === "loading" ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : "Submit review"}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
